import {
  Text,
  useColorModeValue,
  Tooltip,
  Box,
  Button,
  Heading,
  Flex,
  Badge,
  HStack,
  VStack,
  Tag,
  Icon,
  SimpleGrid,
  Progress,
  Wrap,
} from "@chakra-ui/react";
import React, { useState, useMemo } from "react";
import { ComposableMap, Geographies, Geography, ZoomableGroup } from "react-simple-maps";
import {
  FaMapMarkedAlt,
  FaUserInjured,
  FaTimes,
  FaUsers,
  FaGlobeAfrica,
  FaDna,
} from "react-icons/fa";
import tn from "./tn.json";

export const XP_GROUP_COLORS = {
  XPC: "#0d9488", // Teal
  XPA: "#8b5cf6", // Purple
  XPV: "#0284c7", // Sky Blue
  XPD: "#f59e0b", // Amber
  XPE: "#ec4899", // Pink
  XPF: "#10b981", // Emerald
  XPB: "#6366f1", // Indigo
  Unknown: "#94a3b8", // Slate
};

const Map = ({ title, data = {} }) => {
  const [selectedRegion, setSelectedRegion] = useState(null);

  const cardBg = useColorModeValue("white", "navy.700");
  const cardBorder = useColorModeValue("slate.200", "whiteAlpha.100");
  const textColor = useColorModeValue("slate.800", "white");
  const subtitleColor = useColorModeValue("slate.500", "slate.400");
  const defaultGeoFill = useColorModeValue("#cbd5e1", "#1e293b");
  const strokeColor = useColorModeValue("#94a3b8", "#334155");
  const panelBg = useColorModeValue("slate.50", "navy.800");

  // Detect Foreign / Cross-Border cases from pre-elaborated backend data
  const foreignOrigins = useMemo(() => {
    return data?.foreign_origins || [];
  }, [data]);

  // Read stats for current selection (or national overview if null) directly from pre-elaborated backend data
  const activeStats = useMemo(() => {
    if (!data) {
      return {
        label: "National Overview (Tunisia)",
        isForeign: false,
        totalCataloged: 0,
        affectedIndividuals: 0,
        affectedFamilies: 0,
        variants: {},
      };
    }

    if (selectedRegion) {
      if (data.cross_border && data.cross_border[selectedRegion]) {
        return {
          label: data.cross_border[selectedRegion].label,
          isForeign: true,
          totalCataloged: data.cross_border[selectedRegion].total_cataloged,
          affectedIndividuals: data.cross_border[selectedRegion].affected_individuals,
          affectedFamilies: data.cross_border[selectedRegion].affected_families,
          variants: data.cross_border[selectedRegion].variants || {},
        };
      }
      if (data.regions && data.regions[selectedRegion]) {
        return {
          label: data.regions[selectedRegion].label,
          isForeign: false,
          totalCataloged: data.regions[selectedRegion].total_cataloged,
          affectedIndividuals: data.regions[selectedRegion].affected_individuals,
          affectedFamilies: data.regions[selectedRegion].affected_families,
          variants: data.regions[selectedRegion].variants || {},
        };
      }
      return {
        label: selectedRegion,
        isForeign: false,
        totalCataloged: 0,
        affectedIndividuals: 0,
        affectedFamilies: 0,
        variants: {},
      };
    }

    const nat = data.national || {};
    return {
      label: nat.label || "National Overview (Tunisia)",
      isForeign: false,
      totalCataloged: nat.total_cataloged || 0,
      affectedIndividuals: nat.affected_individuals || 0,
      affectedFamilies: nat.affected_families || 0,
      variants: nat.variants || {},
    };
  }, [data, selectedRegion]);

  const handleRegionClick = (geo) => {
    const { shapeName } = geo.properties;
    setSelectedRegion((prev) => (prev === shapeName ? null : shapeName));
  };

  return (
    <Flex direction="column" w="100%" gap={{ base: "12px", md: "24px" }}>
      <Box
        p={{ base: "8px 6px", sm: "14px 10px", md: "22px" }}
        bg={cardBg}
        borderRadius={{ base: "12px", sm: "16px", md: "22px" }}
        border="1px solid"
        borderColor={cardBorder}
        shadow="sm"
        w="100%"
      >
        {/* Header Title */}
        <Flex
          direction={{ base: "column", md: "row" }}
          justify="space-between"
          align={{ base: "start", md: "center" }}
          mb={{ base: "12px", md: "20px" }}
          gap="12px"
        >
          <Box>
            <HStack spacing="10px" wrap="wrap">
              <Heading size="lg" color={textColor} fontWeight="800" letterSpacing="-0.02em">
                Geographical XP Distribution Map
              </Heading>

            </HStack>
            <Text fontSize="sm" color={subtitleColor} mt="4px">
              Interactive geographic mapping of xeroderma pigmentosum cases across Tunisian regions and partner cross-border cohorts.
            </Text>
          </Box>
        </Flex>

        {/* Map & Detail Panel Grid */}
        <Flex direction={{ base: "column", lg: "row" }} gap={{ base: "12px", md: "24px" }} align="stretch">
          {/* Map Container */}
          <Box
            flex="1"
            w="100%"
            minH={{ base: "420px", sm: "480px", md: "560px" }}
            position="relative"
            borderRadius={{ base: "12px", sm: "16px", md: "20px" }}
            bg={panelBg}
            overflow="hidden"
            p={{ base: "4px", sm: "8px", md: "12px" }}
          >
            <ComposableMap
              projection="geoMercator"
              width={550}
              height={700}
              style={{ width: "100%", height: "100%", maxHeight: "650px" }}
              projectionConfig={{
                scale: 3500,
                center: [9.55, 33.85],
              }}
            >
              <ZoomableGroup zoom={1} minZoom={0.8} maxZoom={3}>
                <Geographies geography={tn}>
                  {({ geographies }) =>
                    geographies.map((geo) => {
                      const isSelected = selectedRegion === geo.properties.shapeName;
                      return (
                        <Tooltip key={geo.rsmKey} label={geo.properties.shapeName} hasArrow placement="top">
                          <Geography
                            geography={geo}
                            fill={isSelected ? "#0d9488" : defaultGeoFill}
                            stroke={strokeColor}
                            strokeWidth="0.8"
                            onClick={() => handleRegionClick(geo)}
                            style={{
                              default: { outline: "none", transition: "all 0.2s ease" },
                              hover: { fill: "#06b6d4", outline: "none", cursor: "pointer" },
                              pressed: { fill: "#0f766e", outline: "none" },
                            }}
                          />
                        </Tooltip>
                      );
                    })
                  }
                </Geographies>
              </ZoomableGroup>
            </ComposableMap>

            {/* In-Map Helper Notice */}
            <Box
              position="absolute"
              bottom={{ base: "8px", sm: "16px" }}
              left={{ base: "8px", sm: "16px" }}
              maxW="calc(100% - 16px)"
              bg={cardBg}
              px="12px"
              py="6px"
              borderRadius="10px"
              shadow="xs"
              border="1px solid"
              borderColor={cardBorder}
            >
              <Text fontSize={{ base: "2xs", sm: "xs" }} color={subtitleColor}>
                Click any governorate to focus data • Scroll to zoom
              </Text>
            </Box>
          </Box>

          {/* Region Stats Detail Panel */}
          <Box w={{ base: "100%", lg: "380px" }}>
            <Box
              p={{ base: "14px", sm: "18px", md: "22px" }}
              bg={panelBg}
              borderRadius="20px"
              border="1px solid"
              borderColor={cardBorder}
              h="100%"
              display="flex"
              flexDirection="column"
              justifyContent="space-between"
            >
              <Box>
                <Flex justify="space-between" align="center" mb="16px">
                  <HStack spacing="8px">
                    <Icon as={activeStats.isForeign ? FaGlobeAfrica : FaMapMarkedAlt} color="teal.500" boxSize="18px" />
                    <Heading size="md" color={textColor}>
                      {activeStats.label}
                    </Heading>
                  </HStack>
                  {selectedRegion && (
                    <Button
                      size="xs"
                      variant="ghost"
                      onClick={() => setSelectedRegion(null)}
                      leftIcon={<Icon as={FaTimes} />}
                    >
                      Clear
                    </Button>
                  )}
                </Flex>

                <VStack align="stretch" spacing="12px">
                  {/* Metric 1: Affected Individuals */}
                  <Box p="14px" bg={cardBg} borderRadius="16px" border="1px solid" borderColor={cardBorder}>
                    <HStack justify="space-between" mb="4px">
                      <Text fontSize="xs" fontWeight="700" color={subtitleColor} textTransform="uppercase">
                        Affected Individuals
                      </Text>
                      <Icon as={FaUserInjured} color="red.500" />
                    </HStack>
                    <Text fontSize="3xl" fontWeight="800" color="red.500">
                      {activeStats.affectedIndividuals}
                    </Text>
                  </Box>

                  {/* Metric 2: Affected Families */}
                  <Box p="14px" bg={cardBg} borderRadius="16px" border="1px solid" borderColor={cardBorder}>
                    <HStack justify="space-between" mb="4px">
                      <Text fontSize="xs" fontWeight="700" color={subtitleColor} textTransform="uppercase">
                        Affected Families
                      </Text>
                      <Icon as={FaUsers} color="teal.500" />
                    </HStack>
                    <Text fontSize="3xl" fontWeight="800" color="teal.500">
                      {activeStats.affectedFamilies}
                    </Text>
                  </Box>

                  {/* Metric 3: XP Complementation Groups Distribution [By Colors] */}
                  <Box p="14px" bg={cardBg} borderRadius="16px" border="1px solid" borderColor={cardBorder}>
                    <Text fontSize="xs" fontWeight="700" color={subtitleColor} textTransform="uppercase" mb="10px">
                      XP Complementation Groups Distribution
                    </Text>

                    {Object.keys(activeStats.variants).length === 0 ? (
                      <Text fontSize="xs" color={subtitleColor}>
                        No genotyped complementation group data for this selection.
                      </Text>
                    ) : (
                      <VStack align="stretch" spacing="8px">
                        {/* Segmented Color Distribution Bar */}
                        <Flex h="8px" borderRadius="full" overflow="hidden" w="100%">
                          {Object.entries(activeStats.variants).map(([grp, cnt]) => {
                            const pct = (cnt / (activeStats.affectedIndividuals || 1)) * 100;
                            const color = XP_GROUP_COLORS[grp] || "#94a3b8";
                            return (
                              <Box
                                key={grp}
                                w={`${pct}%`}
                                h="100%"
                                bg={color}
                                title={`${grp}: ${cnt} cases (${pct.toFixed(0)}%)`}
                              />
                            );
                          })}
                        </Flex>

                        {/* Badges List with Group Colors */}
                        <Flex wrap="wrap" gap="6px" pt="4px">
                          {Object.entries(activeStats.variants).map(([grp, cnt]) => {
                            const color = XP_GROUP_COLORS[grp] || "#94a3b8";
                            return (
                              <Tag
                                key={grp}
                                size="md"
                                borderRadius="full"
                                bg={`${color}18`}
                                color={color}
                                border="1px solid"
                                borderColor={`${color}40`}
                                fontWeight="800"
                                fontSize="xs"
                                px="10px"
                                py="4px"
                              >
                                <Box w="7px" h="7px" borderRadius="full" bg={color} mr="6px" />
                                {grp}: {cnt}
                              </Tag>
                            );
                          })}
                        </Flex>
                      </VStack>
                    )}
                  </Box>
                </VStack>
              </Box>

              {/* Cross-Border / Foreign Countries Quick Selector */}
              {foreignOrigins.length > 0 && (
                <Box mt="14px" pt="12px" borderTop="1px solid" borderColor={cardBorder}>
                  <Text fontSize="xs" fontWeight="700" color={subtitleColor} textTransform="uppercase" mb="6px">
                    Cross-Border Cohort (Foreign Origins):
                  </Text>
                  <Wrap spacing="6px">
                    <Button
                      size="xs"
                      borderRadius="full"
                      colorScheme="purple"
                      variant={selectedRegion === "All Cross-Border" ? "solid" : "outline"}
                      onClick={() => setSelectedRegion("All Cross-Border")}
                    >
                      All Cross-Border
                    </Button>
                    {foreignOrigins.map(([country, count]) => (
                      <Button
                        key={country}
                        size="xs"
                        borderRadius="full"
                        colorScheme="purple"
                        variant={selectedRegion === country ? "solid" : "outline"}
                        onClick={() => setSelectedRegion(country)}
                      >
                        {country} ({count})
                      </Button>
                    ))}
                  </Wrap>
                </Box>
              )}
            </Box>
          </Box>
        </Flex>
      </Box>
    </Flex>
  );
};

export default Map;
